import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useOutletContext } from '@remix-run/react';

import { ComponentErrorBoundary } from '~/components/ComponentErrorBoundary';
import FallBack from '~/components/Fallback';
import { Page } from '~/components/Layout';
import { WebThemeProvider } from '~/components/Layout/Context/WebThemeContext';
import type { SignupStyles } from '~/components/Signup/types';
import { ACTIVE_UPGRADE_COOKIE_NAME } from '~/hooks/useUtils/useCookies';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import useWebBuilderCommunicator from '~/hooks/useUtils/useWebBuilderCommunicator';
import remixI18n from '~/i18next.server';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Publication, WebThemeV2 } from '~/interfaces/publication';
import type { PricingDetails } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';
import getSignupWidgetConfigs from '~/pages/index/_utils/getSignupWidgetConfigs';
import CustomUpgradePage from '~/pages/upgrade/CustomUpgradePage';
import PremiumMemberModal from '~/pages/upgrade/components/PremiumMemberModal';
import getPricingDetails from '~/services/api/subscription/getPricingDetails';
import getUpgradePage from '~/services/api/upgrade_pages/getUpgradePage.server';
import { buildHeaders, buildUrl } from '~/services/api/utils';
import { PREMIUM_DISABLED_MESSAGE } from '~/services/api/utils/errorMessages';
import getExamplePublication from '~/services/exampleSite/getExamplePublication.server';
import getExampleUpgradePage from '~/services/exampleSite/getExampleUpgradePage.server';
import { actionSession, loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import getServerTranslation from '~/utils/getServerTranslation/index.server';
import previewAllowedCheck from '~/utils/previewAllowedCheck';
import redirectWithToast from '~/utils/redirectWithToast/index.server';
import setCacheHeaders from '~/utils/setCacheHeaders/index.server';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const publication = data?.publication;
  const metaTitle = data?.metaTitle;
  const metaDescription =
    data?.metaDescription || `Upgrade your subscription to join ${publication?.name}'s premium community.`;
  const renderableFonts = data?.upgradePage?.renderable_fonts || [];
  const links: any = [];

  if (renderableFonts.length > 0) {
    renderableFonts.forEach((font: string) => {
      links.push({
        rel: 'stylesheet',
        href: font,
        tagName: 'link',
      });
    });
  }

  if (data?.upgradePage?.main_bg_image_url) {
    links.push({
      tagName: 'link',
      rel: 'preload',
      href: data?.upgradePage?.main_bg_image_url,
      as: 'image',
    });
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    overrides: {
      title: metaTitle,
      description: metaDescription,
    },
    links: links,
  });
};

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
export const headers: HeadersFunction = ({ actionHeaders, loaderHeaders, parentHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': 'orchid-page',
  };
};

export const action: LoaderFunction = async ({ request }) => {
  try {
    const { session } = await actionSession(request);
    const body = await request.formData();

    const errorMessage = body.get('upgrade_error_message');
    const successMessage = body.get('upgrade_success_message');
    const paymentMethodId = body.get('payment_method');
    const force_three_d_secure = body.get('force_three_d_secure');
    const price_id = body.get('price_id');
    const email = body.get('email');
    const purchaser_name = body.get('purchaser_name');
    const recipient_email = body.get('recipient_email');
    const message = body.get('message');
    const start_at = body.get('start_at');
    const tax_id = body.get('tax_id');
    const tax_id_type = body.get('tax_id_type');
    const premiumOfferId = body.get('premium_offer_id');
    const last_resource_guid = body.get('last_resource_guid');
    const amount_cents = body.get('amount_cents');
    const isGift = body.get('isGift');
    const visitToken = body.get('visit_token');

    // This should never be the case anyways because data is required on the client side
    // Therefore using a generic message rather than translating across languages a specific error message here is ideal
    if ((!isGift && !paymentMethodId) || !price_id || !email) {
      return json({
        toast: {
          status: 'error',
          message: errorMessage,
        },
      });
    }

    const endpoint = buildUrl(`/subscriptions/${isGift === 'true' ? 'gift' : 'upgrade'}`);
    const endpointHeaders = buildHeaders(request, session);

    return await fetch(endpoint, {
      method: 'PUT',
      headers: endpointHeaders,
      body: JSON.stringify({
        price_id,
        amount_cents,
        email,
        purchaser_name,
        recipient_email,
        message,
        start_at,
        tax_id,
        tax_id_type,
        premium_offer_id: premiumOfferId,
        payment_method_id: paymentMethodId,
        force_three_d_secure,
        last_resource_guid,
        visit_token: visitToken,
      }),
    }).then(async (res) => {
      const response = await res.json();

      if (!res.ok) {
        const serverResponseError = response?.error;

        return json({
          toast: {
            status: 'error',
            message: serverResponseError || errorMessage,
          },
        });
      }

      // Occassionally the response will be a 200 but stripe will require an action to further authenticate the user
      const action = response?.upgrade_subscription?.action;
      const requiresAction = action === 'requires_action';
      const requiresConfirmation = action === 'requires_confirmation';
      if (requiresAction || requiresConfirmation) {
        return json({
          action,
          clientSecret: response?.upgrade_subscription?.client_secret,
        });
      }

      return json({
        redirect_url: response?.redirect_url,
        toast: {
          status: 'success',
          message: successMessage,
        },
      });
    });
  } catch (_err) {
    const { defaultErrorMessage } = await getServerTranslation(request);

    return json({
      toast: {
        status: 'error',
        message: defaultErrorMessage,
      },
    });
  }
};

export interface UpgradePageLoaderData {
  publication: Publication;
  upgradePage: UpgradePage;
  pricingDetails: PricingDetails;
  giftPricingDetails: PricingDetails;
  requestUrl: string;
  metaTitle: string;
  metaDescription: string;
  isInitialLoadFromSwarm: boolean;
  webTheme: WebThemeV2;
  hasWebThemesEnabled: boolean;
  signupWidgetConfigs: SignupStyles;
}

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
export const loader: LoaderFunction = async ({ request, params, context }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const isDraft = searchParams.get('version') === 'draft';
  const isExampleSite = searchParams.get('example_site') === 'true';
  const showExampleSite = isDraft && isExampleSite;
  const forceNewWebBuilder = searchParams.get('new_web_builder') === 'true';

  const { instanceToFetch, isInitialLoadFromSwarm } = previewAllowedCheck(request);

  const publication = await context.getPublication(request, session);

  if (!publication?.premium_enabled) {
    return redirect(
      redirectWithToast({
        baseUrl: '/',
        toast: 'error',
        message: PREMIUM_DISABLED_MESSAGE,
      }),
      307,
    );
  }

  const [upgradePage, pricingDetails, webTheme, giftPricingDetails] = await Promise.all([
    getUpgradePage(request, session, { status: instanceToFetch }),
    getPricingDetails(request, session, false),
    context.getWebThemev2(request, session, {
      status: instanceToFetch,
    }),
    getPricingDetails(request, session, true),
  ]);

  const upgradePageHref = publication?.web_template?.external_upgrade_page_href;
  if (upgradePageHref && !isInitialLoadFromSwarm) {
    throw redirect(upgradePageHref, 302);
  }

  const locale = publication?.language || 'en';
  const t = await remixI18n.getFixedT(locale, 'common');

  return json(
    {
      publication: showExampleSite ? getExamplePublication(publication) : publication,
      upgradePage: showExampleSite ? getExampleUpgradePage(upgradePage) : upgradePage,
      pricingDetails,
      giftPricingDetails,
      requestUrl: new URL(request.url).href,
      metaTitle: upgradePage?.seo_section?.title || t('meta.titles.upgrade'),
      metaDescription: upgradePage?.seo_section?.description || '',
      isInitialLoadFromSwarm,
      webTheme: webTheme?.web_theme,
      hasWebThemesEnabled: webTheme?.feature_enabled || forceNewWebBuilder,
      signupWidgetConfigs: getSignupWidgetConfigs({
        webTheme: webTheme?.web_theme,
      }),
    },
    {
      // Note: Keep MaxAge 0 to avoid caching in the browser, we only want to cache via the CDN with sMaxAge.
      headers: setCacheHeaders({
        maxAge: 0,
        sMaxAge: 60 * 60 * 24 * 30, // 30 days
        staleWhileRevalidate: 60,
      }),
    },
  );
};

const Upgrade = () => {
  const {
    publication,
    upgradePage,
    isInitialLoadFromSwarm,
    pricingDetails,
    giftPricingDetails,
    webTheme,
    hasWebThemesEnabled,
  } = useTypedLoaderData<UpgradePageLoaderData>();
  const { cookies, cookiesSet } = useOutletContext<OutletContext>();

  // Throw RefreshPageError if loader data is invalid [WEB-5443]
  if (!publication) {
    throw new Error('RefreshPageError');
  }

  const isUpgradeActive =
    cookiesSet && Boolean(cookies[ACTIVE_UPGRADE_COOKIE_NAME]) && !publication.premium_duplicate_warning_disabled;

  useWebBuilderCommunicator({
    isInitialLoadFromSwarm,
    publicationId: publication.id,
  });

  if (hasWebThemesEnabled) {
    return (
      <Page>
        <ComponentErrorBoundary
          fallbackRender={({ error }) => (
            <FallBack
              errorCode="400"
              customTitle="Stripe Setup Error"
              customDescription="There appears to be an error with the Stripe setup on this page. This could be due to a misconfiguration in your Stripe account with a product or price. Please check your Stripe account and try again. If the issue persists, please contact support."
              error={error}
            />
          )}
        >
          <WebThemeProvider webTheme={webTheme} isEnabled>
            <PremiumMemberModal isUpgradeActive={isUpgradeActive} />

            <CustomUpgradePage
              publication={publication}
              upgradePage={upgradePage}
              pricingDetails={pricingDetails}
              giftPricingDetails={giftPricingDetails}
              isInitialLoadFromSwarm={isInitialLoadFromSwarm}
            />
          </WebThemeProvider>
        </ComponentErrorBoundary>
      </Page>
    );
  }

  return (
    <WebThemeProvider webTheme={webTheme} isEnabled={false}>
      <PremiumMemberModal isUpgradeActive={isUpgradeActive} />
      <CustomUpgradePage
        publication={publication}
        upgradePage={upgradePage}
        pricingDetails={pricingDetails}
        giftPricingDetails={giftPricingDetails}
        isInitialLoadFromSwarm={isInitialLoadFromSwarm}
      />
    </WebThemeProvider>
  );
};

export default Upgrade;
